import type { ReactNode } from "react";
import {
  RequireMembership,
  churchUrl,
  useMyMemberships,
  useOnStaffDoor,
  useTenant,
} from "@how/app-kit";
import { ErrorState, LoadingState } from "@how/ui";
import { env } from "../lib/env";
import { DoorLayout } from "./DoorLayout";
import { staffChurchOf } from "./door-landing";
import { StaffOnlyPage } from "./StaffOnlyPage";

/**
 * The staff dashboard's church guard (ruling P14): RequireMembership as every surface has it (an invitation is offered,
 * no membership is refused), then the staff check below. The member portal keeps RequireMembership alone.
 */
export function StaffMembershipGuard({ children }: { children: ReactNode }) {
  // Asked now, alongside the start-up checks (#286), so the staff check's answer is in the cache when RequireMembership
  // lets RequireStaffAccess render; waiting for it there added a serial round trip to every church page.
  useMyMemberships();
  return (
    <RequireMembership>
      <RequireStaffAccess>{children}</RequireStaffAccess>
    </RequireMembership>
  );
}

/**
 * An active membership here that is member, guardian or volunteer only (my_memberships' has_staff_access) does not
 * open the staff dashboard: the person is told the address is for staff and offered this church's member portal
 * through the hand-off, with a link to their own staff church when they have one (ADR 0068). It fails closed (ruling
 * P18): only a listed, active, staff-side membership here opens the shell; no my_memberships row for this church gets
 * the staff-only page too. Hiding the shell is not the boundary; the database's authorization is.
 */
export function RequireStaffAccess({ children }: { children: ReactNode }) {
  const tenant = useTenant();
  const memberships = useMyMemberships();
  const onDoor = useOnStaffDoor();
  if (memberships.isPending) return <LoadingState label="Checking your staff access" />;
  if (memberships.isError) {
    return (
      <ErrorState
        message="We could not check your staff access."
        onRetry={() => {
          void memberships.refetch();
        }}
      />
    );
  }
  const tenantId = tenant.status === "resolved" ? tenant.tenant.tenantId : null;
  const here = memberships.data.find((m) => m.tenantId === tenantId);
  if (here?.membershipStatus === "active" && here.hasStaffAccess) return <>{children}</>;
  const memberHere = here?.membershipStatus === "active" ? here : undefined;
  const staffChurch = staffChurchOf(memberships.data);
  return (
    <DoorLayout
      staffChurchHref={
        staffChurch === undefined
          ? null
          : churchUrl(staffChurch.slug, "staff_dashboard", env, onDoor)
      }
    >
      <StaffOnlyPage
        churches={memberHere === undefined ? [] : [memberHere]}
        description={
          memberHere === undefined
            ? "Your email has no staff access at this church."
            : `Your email has no staff access at ${memberHere.name}. Open its member portal instead.`
        }
      />
    </DoorLayout>
  );
}
